import { createContext, useContext, useState } from 'react';

const translations = {
  en: {
    // Navigation
    nav_services: 'Services',
    nav_industries: 'Industries',
    nav_team: 'Team',
    nav_process: 'Process',
    nav_contact: 'Contact',
    nav_signin: 'Sign In',
    nav_client_portal: 'Member Portal',
    
    // Hero
    hero_badge: 'Kahnawà:ke Community Network',
    hero_title_1: 'Community-Owned Fibre',
    hero_title_2: 'for Kahnawà:ke',
    hero_description: "Tewatati is the community space of Kahnawà:ke — fast, reliable, locally-operated internet built by and for our community. Every connection strengthens us.",
    hero_cta_portal: 'Member Portal',
    hero_cta_consultation: 'Get Connected',
    
    // Services
    services_title: 'Comprehensive IT Solutions',
    services_description: 'We provide end-to-end technology services to help your business thrive in the digital age.',
    service_cybersecurity: 'Cybersecurity',
    service_cybersecurity_desc: 'Protect your business with advanced security solutions and threat monitoring.',
    service_cloud: 'Cloud Solutions',
    service_cloud_desc: 'Seamless cloud integration and migration for scalable infrastructure.',
    service_managed: 'Managed IT',
    service_managed_desc: 'Comprehensive IT management for a predictable monthly fee.',
    service_support: 'IT Support',
    service_support_desc: '24/7 technical support to keep your operations running smoothly.',
    service_cameras: 'Security Cameras',
    service_cameras_desc: 'Professional surveillance systems for complete property monitoring.',
    service_access: 'Access Control',
    service_access_desc: 'Building access control systems with keycard and biometric solutions.',
    service_voip: 'VoIP Phone Systems',
    service_voip_desc: 'Modern business phone systems with unified communications.',
    service_compliance: 'Compliance',
    service_compliance_desc: 'Navigate complex regulations with expert compliance guidance.',
    service_backup: 'Data Backup & Recovery',
    service_backup_desc: 'Reliable backup solutions and disaster recovery planning to protect your critical data.',
    service_network: 'Network Infrastructure',
    service_network_desc: 'Design, implementation, and management of robust network solutions for optimal connectivity.',
    service_datacenter: 'Data Center Operations',
    service_datacenter_desc: 'Professional data center management with monitoring, maintenance, and optimization.',
    service_desktop: 'Onsite Desktop Support',
    service_desktop_desc: 'Hands-on technical support at your location for hardware and software issues.',
    service_hosting: 'Specialized Hosting',
    service_hosting_desc: 'Custom hosting solutions tailored to your specific application and performance needs.',
    service_venue: 'Large Venue Solutions',
    service_venue_desc: 'Comprehensive IT infrastructure for museums, places of worship, banquet halls, and event spaces.',
    
    // Industries
    industries_title: 'Industry Expertise',
    industries_description: 'Specialized IT solutions tailored to your industry\'s unique requirements.',
    industry_law: 'Law Firms',
    industry_law_desc: 'Secure IT for legal practices',
    industry_accounting: 'Accounting',
    industry_accounting_desc: 'Financial data security',
    industry_medical: 'Medical Clinics',
    industry_medical_desc: 'HIPAA-compliant solutions',
    industry_retail: 'Retail',
    industry_retail_desc: 'POS & inventory systems',
    industry_hospitality: 'Hospitality & Food Service',
    industry_hospitality_desc: 'Restaurant & hotel IT solutions',
    industry_realestate: 'Real Estate',
    industry_realestate_desc: 'Property management systems',
    industry_events: 'Events & Entertainment',
    industry_events_desc: 'Event planners & venues',
    
    // Process
    process_title: 'Our Proven Process',
    process_description: 'A strategic approach to ensure your IT infrastructure is a powerful asset.',
    process_step1_title: 'Discovery',
    process_step1_desc: 'We analyze your current IT setup to identify risks and opportunities.',
    process_step2_title: 'Strategy',
    process_step2_desc: 'We design a custom IT solution tailored to your business needs.',
    process_step3_title: 'Implementation',
    process_step3_desc: 'We deploy and configure your new IT infrastructure.',
    process_step4_title: 'Support',
    process_step4_desc: 'Ongoing monitoring and support ensures optimal performance.',
    
    // Testimonial
    testimonial_quote: '"Jordan MCK IMU is, by far, the best IT specialist I have ever had the pleasure of working with. Not only is he an expert in his field, but he is also a kind, patient, and understanding person."',
    testimonial_author: 'Johane O.',
    testimonial_role: 'Legal Professional, Montreal',
    
    // Contact
    contact_title: 'Reach Out',
    contact_description: 'Questions, ideas, or need a hand? Send us a message — Niá:wen for reaching out.',
    contact_benefit1: 'Proactive monitoring & maintenance',
    contact_benefit2: 'Rapid response times',
    contact_benefit3: 'Flat-rate pricing with no surprises',
    contact_benefit4: 'Local Montreal-based support team',
    contact_form_title: 'Send us a message',
    contact_name: 'Name',
    contact_email: 'Email',
    contact_company: 'Company (optional)',
    contact_message: 'Message',
    contact_submit: 'Send Message',
    contact_sending: 'Sending...',
    contact_success: 'Message sent successfully! We\'ll get back to you shortly.',
    contact_error: 'Failed to send message. Please try again.',
    
    // Footer
    footer_rights: 'All rights reserved. Kahnawà:ke, QC.',
    footer_login: 'Member Login',
    
    // Auth
    auth_welcome_back: 'Welcome back',
    auth_signin_subtitle: 'Sign in to access your client portal',
    auth_email: 'Email',
    auth_password: 'Password',
    auth_signin: 'Sign in',
    auth_signing_in: 'Signing in...',
    auth_no_account: 'Don\'t have an account?',
    auth_create_one: 'Create one',
    auth_create_account: 'Create your account',
    auth_register_subtitle: 'Register to access the client portal',
    auth_full_name: 'Full Name',
    auth_company: 'Company',
    auth_phone: 'Phone (optional)',
    auth_min_password: 'Minimum 10 characters',
    auth_creating: 'Creating account...',
    auth_register: 'Create account',
    auth_have_account: 'Already have an account?',
    auth_signin_link: 'Sign in',
    auth_back_home: 'Back to home',
    
    // Dashboard
    dashboard_welcome: 'Welcome back',
    dashboard_overview: 'Here\'s an overview of your account activity.',
    dashboard_open_tickets: 'Open Tickets',
    dashboard_in_progress: 'in progress',
    dashboard_pending_invoices: 'Pending Invoices',
    dashboard_outstanding: 'CAD outstanding',
    dashboard_pending_quotes: 'Pending Quotes',
    dashboard_total_quotes: 'total quotes',
    dashboard_total_tickets: 'Total Tickets',
    dashboard_all_time: 'All time support requests',
    dashboard_quick_actions: 'Quick Actions',
    dashboard_create_ticket: 'Create Support Ticket',
    dashboard_view_invoices: 'View Invoices',
    dashboard_view_quotes: 'View Quotes',
    dashboard_account_settings: 'Account Settings',
    dashboard_recent_tickets: 'Recent Tickets',
    dashboard_view_all: 'View all',
    dashboard_no_tickets: 'No tickets yet',
    dashboard_create_first: 'Create your first ticket',
    dashboard_urgent_support: 'Need urgent support?',
    dashboard_urgent_desc: 'For critical issues, create a ticket with "Urgent" priority or contact us directly.',
    dashboard_new_ticket: 'New Ticket',
    
    // Tickets
    tickets_title: 'Support Tickets',
    tickets_subtitle: 'Manage your support requests',
    tickets_new: 'New Ticket',
    tickets_search: 'Search tickets...',
    tickets_all_status: 'All Status',
    tickets_open: 'Open',
    tickets_in_progress: 'In Progress',
    tickets_resolved: 'Resolved',
    tickets_closed: 'Closed',
    tickets_all_priority: 'All Priority',
    tickets_low: 'Low',
    tickets_medium: 'Medium',
    tickets_high: 'High',
    tickets_urgent: 'Urgent',
    tickets_no_tickets: 'No tickets found',
    tickets_adjust_filters: 'Try adjusting your filters',
    tickets_create_first: 'Create your first support ticket to get help',
    tickets_create: 'Create Ticket',
    tickets_showing: 'Showing',
    tickets_of: 'of',
    tickets_tickets: 'tickets',
    
    // Create Ticket
    create_ticket_title: 'Create Support Ticket',
    create_ticket_subtitle: 'Submit a new support request to our team',
    create_ticket_back: 'Back to Tickets',
    create_ticket_field_title: 'Title',
    create_ticket_title_placeholder: 'Brief summary of your issue',
    create_ticket_category: 'Category',
    create_ticket_select_category: 'Select category',
    create_ticket_priority: 'Priority',
    create_ticket_description: 'Description',
    create_ticket_description_placeholder: 'Please provide as much detail as possible about your issue, including:\n- Steps to reproduce the problem\n- Any error messages\n- What you expected to happen',
    create_ticket_tip: 'The more details you provide, the faster we can resolve your issue. Include relevant screenshots, error codes, or system information if applicable.',
    create_ticket_cancel: 'Cancel',
    create_ticket_submit: 'Submit Ticket',
    create_ticket_creating: 'Creating...',
    
    // Categories
    category_technical: 'Technical Support',
    category_network: 'Network Issues',
    category_hardware: 'Hardware',
    category_software: 'Software',
    category_security: 'Security',
    category_cloud: 'Cloud Services',
    category_account: 'Account / Billing',
    category_other: 'Other',
    
    // Priorities
    priority_low_desc: 'Minor issue, no business impact',
    priority_medium_desc: 'Moderate impact, workaround available',
    priority_high_desc: 'Significant impact on operations',
    priority_urgent_desc: 'Critical - business stopped',
    
    // Ticket Detail
    ticket_back: 'Back to Tickets',
    ticket_description: 'Description',
    ticket_update_status: 'Update Status',
    ticket_update: 'Update Status',
    ticket_updating: 'Updating...',
    ticket_status_note: 'Note: In production, status updates would sync with your GLPI system.',
    ticket_details: 'Details',
    ticket_category: 'Category',
    ticket_priority: 'Priority',
    ticket_status: 'Status',
    ticket_timeline: 'Timeline',
    ticket_created: 'Ticket Created',
    ticket_last_updated: 'Last Updated',
    ticket_need_help: 'Need urgent assistance? Contact our support team directly.',
    ticket_contact_support: 'Contact Support',
    
    // Invoices
    invoices_title: 'Invoices',
    invoices_subtitle: 'View and manage your invoices',
    invoices_total: 'Total Invoices',
    invoices_outstanding: 'Outstanding',
    invoices_total_paid: 'Total Paid',
    invoices_history: 'Invoice History',
    invoices_no_invoices: 'No invoices yet',
    invoices_will_appear: 'Your invoices will appear here once generated.',
    invoices_number: 'Invoice #',
    invoices_description: 'Description',
    invoices_issued: 'Issued',
    invoices_due_date: 'Due Date',
    invoices_amount: 'Amount',
    invoices_status: 'Status',
    invoices_actions: 'Actions',
    
    // Quotes
    quotes_title: 'Quotes & Proposals',
    quotes_subtitle: 'Review your quotes and proposals',
    quotes_total: 'Total Quotes',
    quotes_pending_value: 'Pending Value',
    quotes_accepted_value: 'Accepted Value',
    quotes_no_quotes: 'No quotes yet',
    quotes_will_appear: 'Your quotes and proposals will appear here.',
    quotes_view_details: 'View Details',
    quotes_accept: 'Accept Quote',
    quotes_created: 'Created',
    quotes_valid_until: 'Valid until',
    
    // Account
    account_title: 'Account Settings',
    account_subtitle: 'Manage your account and company information',
    account_company_info: 'Company Information',
    account_company_name: 'Company Name',
    account_contact_name: 'Contact Name',
    account_phone: 'Phone',
    account_address: 'Address',
    account_city: 'City / Province',
    account_country: 'Country',
    account_save: 'Save Changes',
    account_saving: 'Saving...',
    account_user_profile: 'User Profile',
    account_status: 'Account Status',
    account_active: 'Active',
    account_member_since: 'Member since',
    account_sync_note: 'Your account information is synced with Dolibarr. Changes made here will update your client record.',
    
    // Sidebar
    sidebar_dashboard: 'Dashboard',
    sidebar_tickets: 'Support Tickets',
    sidebar_interventions: 'Interventions',
    sidebar_network: 'Network',
    sidebar_racks: 'Server Racks',
    sidebar_maintenance: 'Maintenance Notices',
    sidebar_assessments: 'Assessments',
    sidebar_invoices: 'Invoices',
    sidebar_quotes: 'Quotes',
    sidebar_proposals: 'Proposals',
    sidebar_messages: 'Messages',
    sidebar_changes: 'Changes',
    sidebar_onboarding: 'Onboarding',
    sidebar_files: 'Files',
    sidebar_kb: 'Knowledge Base',
    sidebar_ipam: 'IPAM',
    sidebar_assets: 'Assets',
    sidebar_faq: 'Help Center',
    sidebar_notifications: 'Notifications',
    sidebar_emails: 'Emails',
    sidebar_contracts: 'Contracts',
    sidebar_quotes: 'Quotes',
    sidebar_service_catalog: 'Service Catalog',
    sidebar_hours: 'Hours Usage',
    sidebar_expenses: 'Expenses',
    sidebar_team: 'Team',
    sidebar_account: 'Account',
    sidebar_signout: 'Sign out',
    
    // Team Management
    team_title: 'Team Management',
    team_subtitle: 'Manage users for',
    team_add_member: 'Add Team Member',
    team_total_members: 'Total Members',
    team_managers: 'Managers',
    team_active_members: 'Active Members',
    team_member: 'Member',
    team_role: 'Role',
    team_language: 'Language',
    team_status: 'Status',
    team_actions: 'Actions',
    team_manager: 'Manager',
    team_user: 'User',
    team_active: 'Active',
    team_inactive: 'Inactive',
    team_you: 'You',
    team_edit: 'Edit',
    team_edit_permissions: 'Edit Permissions',
    team_deactivate: 'Deactivate',
    team_activate: 'Activate',
    team_no_members: 'No team members found',
    team_full_name: 'Full Name',
    team_full_name_placeholder: 'John Smith',
    team_email: 'Email',
    team_password: 'Password',
    team_password_placeholder: 'Minimum 10 characters',
    team_password_hint: 'The user will receive this password via email',
    team_phone: 'Phone',
    team_make_manager: 'Make Manager',
    team_new_member_info: 'A welcome email with login credentials will be sent to the new team member.',
    team_cancel: 'Cancel',
    team_create: 'Create Member',
    team_save: 'Save Changes',
    team_edit_member: 'Edit Team Member',
    team_email_readonly: 'Email cannot be changed',
    team_permissions_for: 'Permissions for',
    team_perm_own_tickets: 'View Own Tickets Only',
    team_perm_own_tickets_desc: 'If OFF, can view all company tickets',
    team_perm_create_tickets: 'Can Create Tickets',
    team_perm_modify_tickets: 'Can Modify Tickets',
    team_perm_view_invoices: 'Can View Invoices',
    team_perm_view_proposals: 'Can View Proposals',
    team_perm_accept_proposals: 'Can Accept Proposals',
    team_perm_readonly: 'Read-Only Mode',
    team_perm_readonly_desc: 'Overrides all to view-only',
    team_save_permissions: 'Save Permissions',
    team_access_denied: 'Access Denied',
    team_manager_only: 'Only managers can access team management.',
    team_not_manager: 'Only managers can access team management',
    team_fetch_error: 'Failed to load team members',
    team_fill_required: 'Please fill all required fields',
    team_password_min: 'Password must be at least 10 characters',
    team_member_created: 'Team member created successfully',
    team_create_error: 'Failed to create team member',
    team_member_updated: 'Team member updated successfully',
    team_update_error: 'Failed to update team member',
    team_permissions_updated: 'Permissions updated successfully',
    team_member_deactivated: 'Team member deactivated',
    team_member_activated: 'Team member activated',
    
    // Notifications
    notif_page_title: 'Notification Settings',
    notif_page_desc: 'Manage how you receive notifications',
    notif_push_title: 'Push Notifications',
    notif_push_desc: 'Receive instant notifications on your device',
    notif_push_enabled: 'Enabled',
    notif_push_disabled: 'Disabled',
    notif_push_will_receive: 'You will receive push notifications',
    notif_push_enable_prompt: 'Enable to receive instant alerts',
    notif_push_not_supported: 'Push notifications are not supported on this device/browser',
    notif_push_blocked: 'Notifications Blocked',
    notif_push_blocked_desc: 'Enable notifications in your browser settings to receive alerts',
    notif_prefs_title: 'Notification Preferences',
    notif_prefs_desc: 'Choose which notifications you want to receive',
    notif_ticket_updates: 'Ticket Updates',
    notif_ticket_updates_desc: 'When your ticket status changes',
    notif_ticket_resolved: 'Ticket Resolved',
    notif_ticket_resolved_desc: 'When your ticket is resolved',
    notif_new_messages: 'New Messages',
    notif_new_messages_desc: 'When you receive a new message',
    notif_warranty_alerts: 'Warranty Alerts',
    notif_warranty_alerts_desc: 'When asset warranties are expiring',
    notif_new_tickets: 'New Tickets',
    notif_new_tickets_desc: 'When a new support ticket is created',
    notif_high_priority: 'High Priority Tickets',
    notif_high_priority_desc: 'Urgent and high priority tickets',
    notif_sla_warnings: 'SLA Warnings',
    notif_sla_warnings_desc: 'When tickets are approaching SLA breach',
    notif_new_customers: 'New Customers',
    notif_new_customers_desc: 'When a new customer registers',
    notif_client_messages: 'Client Messages',
    notif_client_messages_desc: 'When clients send messages',
    notif_system_alerts: 'System Alerts',
    notif_system_alerts_desc: 'Integration errors and system issues',
    
    // Push Admin
    push_admin_title: 'Push Notifications',
    push_admin_desc: 'Manage and monitor push notifications',
    push_send_btn: 'Send Notification',
    push_refresh: 'Refresh',
    push_total_subs: 'Total Subscribers',
    push_sent_today: 'Sent Today',
    push_this_week: 'This Week',
    push_failed: 'Failed',
    push_status: 'Status',
    push_user_type: 'User Type',
    push_clear_logs: 'Clear Old Logs',
    push_log_title: 'Notification Log',
    push_no_logs: 'No notification logs found',
    push_vapid_warning: 'VAPID Keys Not Configured',
    push_vapid_warning_desc: 'Push notifications are being logged but not sent. Add VAPID_PUBLIC_KEY and VAPID_PRIVATE_KEY to your environment variables.',
    push_send_title: 'Send Push Notification',
    push_title: 'Title',
    push_message: 'Message',
    push_link_url: 'Link URL',
    push_target: 'Target',
    push_target_all: 'All Subscribers',
    push_target_company: 'Specific Company',
    push_target_user: 'Specific User',
    push_sending: 'Sending...',
    
    // Careers
    careers_join: 'Join Our Team',
    careers_title: 'Careers at MCK IMU',
    careers_subtitle: 'Build your career with a passionate team redefining IT services for businesses across Canada.',
    careers_life_title: 'Life at MCK IMU',
    careers_life_subtitle: 'We believe great people build great solutions. Here\'s why our team loves working here.',
    careers_positions_title: 'Open Positions',
    careers_positions_count: 'open position',
    careers_positions_count_plural: 'open positions',
    careers_no_positions: 'No open positions at the moment.',
    careers_no_positions_detail: 'No open positions right now, but check back soon!',
    careers_perk_health: 'Health & Wellness',
    careers_perk_health_desc: 'Comprehensive health, dental, and vision coverage for you and your family.',
    careers_perk_growth: 'Growth & Learning',
    careers_perk_growth_desc: 'Annual professional development budget and certifications supported.',
    careers_perk_flex: 'Flexible Work',
    careers_perk_flex_desc: 'Hybrid and remote options with flexible hours to suit your lifestyle.',
    careers_perk_balance: 'Work-Life Balance',
    careers_perk_balance_desc: 'Generous PTO, paid holidays, and wellness days.',
    careers_perk_team: 'Great Team',
    careers_perk_team_desc: 'Collaborative, supportive team with regular social events.',
    careers_perk_pay: 'Competitive Pay',
    careers_perk_pay_desc: 'Market-leading compensation with annual reviews and bonuses.',
    careers_job_full_time: 'Full-Time',
    careers_job_part_time: 'Part-Time',
    careers_job_contract: 'Contract',
    careers_job_internship: 'Internship',
    careers_description: 'Description',
    careers_requirements: 'Requirements',
    careers_benefits: 'Benefits',
    careers_close: 'Close',
    careers_apply_now: 'Apply Now',
    careers_apply: 'Apply',
    careers_submitted: 'Application Submitted!',
    careers_thank_you: 'Thank you for your application!',
    careers_confirm_email: 'We\'ve sent a confirmation email. Our team will review your application shortly.',
    careers_full_name: 'Full Name',
    careers_phone: 'Phone',
    careers_cv: 'CV / Resume',
    careers_cv_upload: 'Upload CV (PDF, DOC, DOCX - max 10MB)',
    careers_cv_required: 'Please upload your CV',
    careers_cover_letter: 'Cover Letter (optional)',
    careers_cover_letter_upload: 'Upload cover letter file (optional)',
    careers_message: 'Additional Message (optional)',
    careers_message_placeholder: 'Tell us why you\'re interested in this role...',
    careers_submit: 'Submit Application',
    careers_submitting: 'Submitting...',
    careers_success: 'Application submitted successfully!',
    careers_error: 'Failed to submit application',
    careers_error_generic: 'Error submitting application',
    careers_name_email_required: 'Name and email are required',
    careers_footer: 'All rights reserved.',

    // Common
    common_loading: 'Loading...',
    common_error: 'An error occurred',
    common_success: 'Success',
    common_cancel: 'Cancel',
    common_save: 'Save',
    common_delete: 'Delete',
    common_edit: 'Edit',
    common_view: 'View',
    common_required: 'Required',
  },
};

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  const [language] = useState('en');

  const t = (key) => translations.en[key] || key;

  // Kept as a no-op for any code still calling them — the UI toggle has
  // been removed and the portal is locked to English.
  const setLanguage = () => {};
  const toggleLanguage = () => {};

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
